import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";

// Layouts
import DashboardLayout from "./components/layout/DashboardLayout";

// Centralised role sets
import { BACKOFFICE_ROLES_ARRAY, CUSTOMER_FACING_ROLES_ARRAY } from "./lib/roles";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Dashboard from "./pages/dashboard/Dashboard";
import PropertyList from "./pages/properties/PropertyList";
import PropertyDetail from "./pages/properties/PropertyDetail";
import PropertyShareBridge from "./pages/properties/PropertyShareBridge";
import Appointments from "./pages/appointments/Appointments";
import Bookings from "./pages/bookings/Bookings";
import CreateBooking from "./pages/bookings/CreateBooking";
import Wallet from "./pages/wallet/Wallet";
import Profile from "./pages/profile/Profile";
import Referrals from "./pages/referrals/Referrals";
import AgentCommissionClaims from "./pages/claims/AgentCommissionClaims";
import PaymentSlips from "./pages/claims/PaymentSlips";

import PropertyListPublic from "./pages/properties/PropertyListPublic";
import ReferralLanding from "./pages/referral/ReferralLanding";
import LoanPortal from "./pages/loan/LoanPortal";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProperties from "./pages/admin/AdminProperties";
import AdminLoanCMS from "./pages/admin/loans/AdminLoanCMS";
import AdminAiconSync from "./pages/admin/AdminAiconSync";
import RBAC from "./pages/admin/rbac/RBAC";
import DirectBankPayouts from "./pages/admin/payouts/DirectBankPayouts";
import LegacyCommissions from "./pages/admin/legacy/LegacyCommissions";
import UnitAvailability from "./pages/admin/UnitAvailability";
import AgentManagement from "./pages/admin/AgentManagement";
import AgentDetails from "./pages/admin/AgentDetails";
import CommEngineOps from "./pages/admin/commission/CommEngineOps";
import AdminIncentives from "./pages/admin/incentives/AdminIncentives";
import IncentivesHub from "./pages/incentives/IncentivesHub";
import AdminKYC from "./pages/admin/AdminKYC";
import AdminWithdrawals from "./pages/admin/AdminWithdrawals";
import AdminConfig from "./pages/admin/AdminConfig";
import AdminPdfTemplates from "./pages/admin/AdminPdfTemplates";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminBookingTemplates from "./pages/admin/AdminBookingTemplates";
import AdminTeamMembers from "./pages/admin/AdminTeamMembers";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import AdminPushNotifications from "./pages/admin/AdminPushNotifications";
import AdminDeveloperTemplates from "./pages/admin/AdminDeveloperTemplates";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminCommissionClaims from "./pages/admin/AdminCommissionClaims";
import AdminRankConfig from "./pages/admin/AdminRankConfig";
import AdminPromotions from "./pages/admin/AdminPromotions";
import AdminCustomerManagement from "./pages/admin/AdminCustomerManagement";
import AdminCustomerDetails from "./pages/admin/AdminCustomerDetails";
import PaymentVerification from "./pages/admin/PaymentVerification";
import AdminOpenLeads from "./pages/admin/AdminOpenLeads";
import OpenLeads from "./pages/leads/OpenLeads";
import CustomerSurveys from "./pages/surveys/CustomerSurveys";
import PerksPage from "./pages/perks/PerksPage";
import AdminPerks from "./pages/admin/AdminPerks";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminRefunds from "./pages/admin/AdminRefunds";
import AdminAIConciergeSettings from "./pages/admin/AdminAIConciergeSettings";

// Banker Portal (separate top-level — not part of admin layout)
import BankerLogin from "./pages/banker/BankerLogin";
import BankerSetup from "./pages/banker/BankerSetup";
import BankerLayout from "./pages/banker/BankerLayout";
import BankerInbox from "./pages/banker/BankerInbox";
import BankerCaseDetail from "./pages/banker/BankerCaseDetail";
import BankerOutsourceCaseDetail from "./pages/banker/BankerOutsourceCaseDetail";
import OutsourceLogin from "./pages/outsource/OutsourceLogin";
import OutsourceSetup from "./pages/outsource/OutsourceSetup";
import OutsourceDashboard from "./pages/outsource/OutsourceDashboard";
import OutsourceCaseDetail from "./pages/outsource/OutsourceCaseDetail";
import OutsourceRoster from "./pages/outsource/OutsourceRoster";

// Customer Loan Offers
import CustomerLoanOffers from "./pages/loans/CustomerLoanOffers";
// V4.24.46 — Downstream loan screens (Web parity with Mobile next_screen)
import LoanDetail from "./pages/loans/LoanDetail";
import LoanPickBank from "./pages/loans/LoanPickBank";
import LoanLoMeetup from "./pages/loans/LoanLoMeetup";
import LoanQuestionnaireWrapper from "./pages/loans/LoanQuestionnaireWrapper";
import LoanScoreAndPath from "./pages/loans/LoanScoreAndPath";
import LoanPickOutsource from "./pages/loans/LoanPickOutsource";
import LoanPickOutsourceBankers from "./pages/loans/LoanPickOutsourceBankers";

import { EntityProvider } from "./context/EntityContext";
import ChatBubble from "./components/ChatBubble";
import { useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/common/ErrorBoundary";

function ChatBubbleWrapper() {
  const { user } = useAuth();
  if (!user || !['customer', 'agent'].includes(user.role)) return null;
  return <ChatBubble />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <EntityProvider>
          <div className="App">
            <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Public Property Routes */}
            <Route path="/browse-properties" element={<PropertyListPublic />} />
            <Route path="/ref/:code" element={<ReferralLanding />} />

            {/* V4.16.11 — Mobile Share-Sheet bridge.
                The mobile API returns share URLs in the shape
                  /property/{refCode}/{propertyId}  (with attribution) or
                  /property/{propertyId}            (anonymous).
                The bridge attempts a `samrumah://property/{id}` deep link on
                mobile UA, then falls back to the web detail page so desktop
                visitors and uninstalled-app visitors still see the listing. */}
            <Route path="/property/:refCode/:id" element={<PropertyShareBridge />} />
            <Route path="/property/:refCode" element={<PropertyShareBridge />} />

            {/* Customer Loan Magic-Link Portal (token-gated, no login) */}
            <Route path="/loan-portal/:token" element={<LoanPortal />} />

            {/* Banker Portal (separate top-level — independent auth via banker_token) */}
            <Route path="/banker/login" element={<BankerLogin />} />
            <Route path="/banker/setup" element={<BankerSetup />} />
            <Route element={<BankerLayout />}>
              <Route path="/banker/inbox" element={<BankerInbox />} />
              <Route path="/banker/cases/:id" element={<BankerCaseDetail />} />
              <Route path="/banker/outsource-cases/:assignment_id" element={<BankerOutsourceCaseDetail />} />
            </Route>

            {/* Outsource Partner Portal (separate top-level — independent auth via outsource_token) */}
            <Route path="/outsource/login" element={<OutsourceLogin />} />
            <Route path="/outsource/setup" element={<OutsourceSetup />} />
            <Route path="/outsource/dashboard" element={<OutsourceDashboard />} />
            <Route path="/outsource/cases/:case_id" element={<OutsourceCaseDetail />} />
            <Route path="/outsource/roster" element={<OutsourceRoster />} />

            {/* V4.9.3 — Shared authenticated routes (any role can access).
                Declared FIRST so React-Router doesn't match the role-gated
                copies further down and bounce PIC / Admin / Account users
                back to /admin. */}
            <Route element={<DashboardLayout />}>
              <Route path="/profile" element={<Profile />} />
            </Route>

            {/* Customer/Agent Routes */}
            <Route element={<DashboardLayout allowedRoles={CUSTOMER_FACING_ROLES_ARRAY} />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/properties" element={<PropertyList />} />
              <Route path="/agent-properties" element={<PropertyList />} />
              <Route path="/properties/:id" element={<PropertyDetail />} />
              <Route path="/my-appointments" element={<Appointments />} />
              <Route path="/appointments" element={<Appointments />} />
              <Route path="/my-bookings" element={<Bookings />} />
              <Route path="/loan-offers" element={<CustomerLoanOffers />} />
              {/* V4.24.46 — Customer/agent loan flow screens (deep-link parity with Mobile) */}
              <Route path="/loan/:id" element={<LoanDetail />} />
              <Route path="/loan/:id/pick-bank" element={<LoanPickBank />} />
              <Route path="/loan/:id/lo-meetup" element={<LoanLoMeetup />} />
              <Route path="/loan/:id/questionnaire" element={<LoanQuestionnaireWrapper />} />
              <Route path="/loan/:id/score" element={<LoanScoreAndPath />} />
              <Route path="/loan/:id/pick-outsource" element={<LoanPickOutsource />} />
              <Route path="/loan/:id/pick-outsource-bankers" element={<LoanPickOutsourceBankers />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/bookings/create" element={<CreateBooking />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/commission-claims" element={<AgentCommissionClaims />} />
              <Route path="/incentives" element={<IncentivesHub />} />
              <Route path="/payment-slips" element={<PaymentSlips />} />
              <Route path="/open-leads" element={<OpenLeads />} />
              <Route path="/surveys" element={<CustomerSurveys />} />
              <Route path="/my-team" element={<Referrals />} />
              <Route path="/performance" element={<Dashboard />} />
              <Route path="/perks" element={<PerksPage />} />
            </Route>

            {/* Admin/PIC/Account Routes */}
            <Route element={<DashboardLayout allowedRoles={BACKOFFICE_ROLES_ARRAY} />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/properties" element={<AdminProperties />} />
              <Route path="/admin/loans" element={<AdminLoanCMS />} />
              <Route path="/admin/aicon-sync" element={<AdminAiconSync />} />
              <Route path="/admin/units" element={<UnitAvailability />} />
              <Route path="/admin/agents" element={<AgentManagement />} />
              <Route path="/admin/agents/:id" element={<AgentDetails />} />
              <Route path="/admin/commission-engine" element={<CommEngineOps />} />
              <Route path="/admin/incentives" element={<AdminIncentives />} />
              <Route path="/admin/customers" element={<AdminCustomerManagement />} />
              <Route path="/admin/customers/:id" element={<AdminCustomerDetails />} />
              <Route path="/admin/kyc" element={<AdminKYC />} />
              <Route path="/admin/appointments" element={<Appointments />} />
              <Route path="/admin/bookings" element={<AdminBookings />} />
              <Route path="/admin/payment-verification" element={<PaymentVerification />} />
              <Route path="/admin/commission-claims" element={<AdminCommissionClaims />} />
              <Route path="/admin/open-leads" element={<AdminOpenLeads />} />
              <Route path="/admin/withdrawals" element={<AdminWithdrawals />} />
              <Route path="/admin/payouts/direct-bank" element={<DirectBankPayouts />} />
              <Route path="/admin/legacy-commissions" element={<LegacyCommissions />} />
              <Route path="/admin/config" element={<AdminConfig />} />
              <Route path="/admin/pdf-templates" element={<AdminPdfTemplates />} />
              <Route path="/admin/booking-templates" element={<AdminBookingTemplates />} />
              <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
              <Route path="/admin/push-notifications" element={<AdminPushNotifications />} />
              <Route path="/admin/developer-templates" element={<AdminDeveloperTemplates />} />
              <Route path="/admin/categories" element={<AdminCategories />} />
              <Route path="/admin/rank-config" element={<AdminRankConfig />} />
              <Route path="/admin/team-members" element={<AdminTeamMembers />} />
              <Route path="/admin/promotions" element={<AdminPromotions />} />
              <Route path="/admin/perks" element={<AdminPerks />} />
              <Route path="/admin/banners" element={<AdminBanners />} />
              <Route path="/admin/refunds" element={<AdminRefunds />} />
              <Route path="/admin/ai-concierge" element={<AdminAIConciergeSettings />} />
              <Route path="/admin/rbac" element={<RBAC />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/* ChatBubble must live INSIDE <BrowserRouter> — it calls
              useNavigate() to open property pages from inline cards. */}
          <ChatBubbleWrapper />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </div>
        </EntityProvider>
      </NotificationProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
